<div #toastOutlet></div>

<app-modal
  #deleteModal
  label="Delete Test Set"
  description="Are you sure you want to delete this test set? This action cannot be undone."
  (confirmed)="confirmDelete()" />

<div class="flex min-h-0 flex-col w-full lg:p-11 p-6 card">
  <div
    class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
    <h1 data-testid="headerTitle" class="text-2xl lg:text-4xl font-bold">
      Test Sets
    </h1>
    <app-button
      data-testid="createTestSetButton"
      color="primary"
      label="+ Create"
      (clicked)="goToCreateTestSet()" />
  </div>

  <div
    class="hidden lg:px-8 px-5 pb-5 lg:text-2xl text-lg font-semibold lg:flex">
    <div class="flex-1 pr-6">
      <p>Title & Description</p>
    </div>
    <div class="w-[30%] grid grid-cols-2 gap-4">
      <p class="text-center">Difficulty</p>
      <p class="text-center">Durations</p>
    </div>
    <div class="w-[30%]">
      <p class="text-center">Actions</p>
    </div>
  </div>

  <div data-testid="test-sets-list">
    @if (isTestSetsLoading()) {
    <div class="flex items-center justify-center h-full">
      <span class="loading loading-dots loading-lg"></span>
    </div>
    } @else if (testSetsData().length === 0) {
    <div>No test sets found.</div>
    } @else { @for(testSet of testSetsData(); track testSet.testSetId) {
    <div
      class="w-full bg-base-100 border border-gray-200 rounded-lg shadow-sm lg:px-8 px-5 py-5 flex items-center mb-4"
      data-testid="test-sets-card">
      <!-- Desktop Layout -->
      <div class="hidden lg:flex items-center w-full">
        <div class="flex-1">
          <h3 class="text-xl font-bold text-gray-900 mb-2">
            {{testSet.title}}
          </h3>
          <p
            class="text-gray-600 text-sm truncate-2-lines"
            id="test-sets-description">
            {{testSet.description}}
          </p>
        </div>
        <div class="w-[30%] grid grid-cols-2 gap-4 items-center">
          <div class="flex justify-center">
            <app-badge [value]="testSet.difficulty" [size]="'lg'" />
          </div>
          <p class="text-xl font-semibold text-center">
            {{testSet.duration || 0}} minutes
          </p>
        </div>
        <div
          class="w-[30%] flex justify-center gap-2"
          [attr.title]="testSet.title">
          <app-button
            [attr.data-testid]="'view-test-set-button'"
            color="primary"
            label="View"
            (clicked)="goToViewTestSet(testSet.testSetId)" />
          <app-button
            [attr.data-testid]="'edit-test-set-button'"
            color="secondary"
            label="Edit"
            (clicked)="goToEditTestSet(testSet.testSetId)"
            [disabled]="testSet.inUsed!" />
          <app-button
            [attr.data-testid]="'delete-test-set-button'"
            label="Delete"
            (clicked)="onDeleteTestSet(testSet.testSetId)"
            [disabled]="testSet.inUsed!" />
        </div>
      </div>

      <!-- Mobile Layout -->
      <div class="lg:hidden space-y-4 w-full">
        <div>
          <h3 class="text-lg font-bold text-gray-900 mb-2">
            {{testSet.title}}
          </h3>
          <p class="text-gray-600 text-sm truncate-3-lines">
            {{testSet.description}}
          </p>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center gap-4">
            <div class="flex items-center gap-1">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M18.8997 12.2L18.1897 8.99999L18.8997 5.79999C19.0114 5.29255 18.7159 4.7841 18.2197 4.62999L15.0897 3.62999L12.6797 1.41999C12.2962 1.06427 11.7033 1.06427 11.3197 1.41999L8.90972 3.63999L5.77972 4.63999C5.28812 4.79381 4.9938 5.29589 5.09972 5.79999L5.80972 8.99999L5.09972 12.2C4.98801 12.7074 5.28352 13.2159 5.77972 13.37L7.99972 14.07V21C8.00161 21.3668 8.20423 21.7032 8.52764 21.8764C8.85105 22.0496 9.24333 22.0318 9.54972 21.83L11.9997 20.2L14.4497 21.83C14.7561 22.0318 15.1484 22.0496 15.4718 21.8764C15.7952 21.7032 15.9978 21.3668 15.9997 21V14.07L18.2197 13.37C18.7159 13.2159 19.0114 12.7074 18.8997 12.2ZM11.4497 18.2L9.99972 19.2V15.36L11.3197 16.58C11.5057 16.749 11.7484 16.8418 11.9997 16.84C12.2501 16.8372 12.4913 16.745 12.6797 16.58L13.9997 15.36V19.13L12.5497 18.2C12.216 17.9802 11.7835 17.9802 11.4497 18.2ZM13.8997 12.76C14.0105 12.6621 14.1397 12.5873 14.2797 12.54L16.7497 11.74L16.1797 9.21999C16.1498 9.07484 16.1498 8.92513 16.1797 8.77999L16.7497 6.25999L14.2797 5.48999C14.1397 5.44269 14.0105 5.36789 13.8997 5.26999L11.9997 3.51999L10.0997 5.26999C9.98897 5.36789 9.85977 5.44269 9.71972 5.48999L7.24972 6.25999L7.81972 8.77999C7.84963 8.92513 7.84963 9.07484 7.81972 9.21999L7.24972 11.74L9.71972 12.51C9.85977 12.5573 9.98897 12.6321 10.0997 12.73L11.9997 14.48L13.8997 12.76Z"
                  fill="#231F20" />
              </svg>
              <app-badge [value]="testSet.difficulty" />
            </div>
            <div class="flex items-center gap-1">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22C17.5228 22 22 17.5228 22 12C22 9.34784 20.9464 6.8043 19.0711 4.92893C17.1957 3.05357 14.6522 2 12 2ZM12 20C7.58172 20 4 16.4183 4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12C20 14.1217 19.1571 16.1566 17.6569 17.6569C16.1566 19.1571 14.1217 20 12 20ZM15 14C15.4482 14.0234 15.857 13.7454 16 13.32C16.0836 13.0614 16.0583 12.78 15.9301 12.5404C15.8018 12.3008 15.5815 12.1238 15.32 12.05L13 11.28V7C13 6.44772 12.5523 6 12 6C11.4477 6 11 6.44772 11 7V12C10.9989 12.4299 11.2727 12.8124 11.68 12.95L14.68 13.95C14.784 13.9805 14.8916 13.9973 15 14Z"
                  fill="#231F20" />
              </svg>
              <span class="text-sm font-medium"
                >{{testSet.duration || 0}} minutes</span
              >
            </div>
          </div>
        </div>

        <div class="flex gap-2 pt-2">
          <app-button
            color="primary"
            label="View"
            (clicked)="goToViewTestSet(testSet.testSetId)" />
          <app-button
            color="secondary"
            label="Edit"
            (clicked)="goToEditTestSet(testSet.testSetId)"
            [disabled]="testSet.inUsed!" />
          <app-button
            label="Delete"
            (clicked)="onDeleteTestSet(testSet.testSetId)"
            [disabled]="testSet.inUsed!" />
        </div>
      </div>
    </div>
    } }
  </div>
</div>
